import { CountryCode, getCountries, getCountryCallingCode } from 'libphonenumber-js';
import { ShareholderData, UserInfos } from '../Interfaces';
import { formatApiDateToInput } from '../../../../utils/date';
import { ownerFromBelongsToShareholder, normalizeDocumentType } from '../../../../utils/shareholderPersonalDocs';

const ALLOWED_DOCUMENT_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
];

/** Empty UserInfos for useFieldArray append — avoids validating Required errors on mount. */
export const createEmptyUserInfos = (): UserInfos => ({
  name: '',
  occupation: '',
  document_number: '',
  documentType: '',
  addressProofOwner: '',
  addressProofKinship: '',
  gender: '',
  dateOfBirth: '',
  phone: '',
  ddi: '',
  email: '',
  nationality: '',
  passportNo: '',
  expiryDate: '',
  passportIssueCountry: '',
  issueDate: '',
  address1: '',
  address2: '',
  postalCode: '',
  city: '',
  state: '',
  neighborhood: '',
  country: '',
  shareIssuanceDate: '',
  classOfShares: '',
  numberOfShares: '',
  sharesValue: '',
  passport: null,
  document1: null,
  proveAddress: null,
  passportExistingName: '',
  document1ExistingName: '',
  proveAddressExistingName: ''
});

const firstRecord = (value: unknown): Record<string, any> => {
  if (Array.isArray(value)) {
    return (value[0] as Record<string, any>) || {};
  }
  if (value && typeof value === 'object') {
    return value as Record<string, any>;
  }
  return {};
};

export const toShareCountInput = (value: unknown): string => {
  if (value == null || value === '') {
    return '';
  }
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    return '';
  }
  return String(Math.trunc(parsed));
};

export const formatNationalPhone = (ddd?: string | null, number?: string | null): string => {
  const dddDigits = String(ddd || '').replace(/\D/g, '');
  const numberDigits = String(number || '').replace(/\D/g, '');
  const combined = `${dddDigits}${numberDigits}`;

  if (combined.length === 11) {
    return `(${combined.slice(0, 2)}) ${combined.slice(2, 7)}-${combined.slice(7)}`;
  }
  if (combined.length === 10) {
    return `(${combined.slice(0, 2)}) ${combined.slice(2, 6)}-${combined.slice(6)}`;
  }
  return numberDigits;
};

export const countryCodeFromDdi = (ddi?: string | null): CountryCode => {
  const digits = String(ddi || '').replace(/\D/g, '');
  if (!digits || digits === '55') {
    return 'BR';
  }
  const match = getCountries().find((country) => getCountryCallingCode(country) === digits);
  return (match as CountryCode) || 'BR';
};

export const fileNameFromDocument = (data: Record<string, any>): string => {
  if (!data) {
    return '';
  }
  if (typeof data.document_name === 'string' && data.document_name) {
    return data.document_name;
  }
  if (typeof data.name === 'string' && data.name) {
    return data.name;
  }
  const fileUrl = data.document_url || data.meta_data?.s3Path;
  if (typeof fileUrl !== 'string' || !fileUrl) {
    return '';
  }
  try {
    return decodeURIComponent(fileUrl.split('?')[0].split('/').pop() || '');
  } catch {
    return fileUrl.split('/').pop() || '';
  }
};

export const isAllowedShareholderFileType = (value: unknown): boolean => {
  if (!value) {
    return true;
  }
  if (value instanceof File) {
    return ALLOWED_DOCUMENT_TYPES.includes(value.type);
  }
  return true;
};

export const isAllowedShareholderFileSize = (value: unknown): boolean => {
  if (!value || !(value instanceof File)) {
    return true;
  }
  return value.size <= 3 * 1024 * 1024;
};

export const hasShareholderDocument = (value: unknown, existingName?: string | null): boolean => {
  if (value instanceof File) {
    return true;
  }
  if (typeof existingName === 'string' && existingName.trim()) {
    return true;
  }
  if (value && typeof value === 'object' && 'url' in (value as object)) {
    return true;
  }
  return false;
};

export const convertObjectToFile = async (data: any): Promise<File | null> => {
  try {
    const record = firstRecord(data);
    const fileUrl = record.document_url || record.meta_data?.s3Path;

    if (!fileUrl) {
      return null;
    }

    const response = await fetch(fileUrl);
    if (!response.ok) {
      console.error('Erro ao buscar o arquivo:', response.statusText);
      return null;
    }

    const blob = await response.blob();
    const fileName = fileNameFromDocument(record) || fileUrl.split('/').pop() || 'arquivo';
    const fileType = record.type || blob.type;
    return new File([blob], fileName, { type: fileType });
  } catch (error) {
    console.error('Erro ao converter objeto em File:', error);
    return null;
  }
};

export const collectShareholderFileNames = (items: UserInfos[]): { [key: string]: string } => {
  const names: { [key: string]: string } = {};

  items.forEach((item, index) => {
    const passportName = (item.passport instanceof File && item.passport.name) || item.passportExistingName;
    const documentName = (item.document1 instanceof File && item.document1.name) || item.document1ExistingName;
    const addressName = (item.proveAddress instanceof File && item.proveAddress.name) || item.proveAddressExistingName;

    if (passportName) {
      names[`passport-${index}`] = passportName;
    }
    if (documentName) {
      names[`document1-${index}`] = documentName;
    }
    if (addressName) {
      names[`proveAddress-${index}`] = addressName;
    }
  });

  return names;
};

export const padUserInfosList = (items: UserInfos[] = [], totalSlots: number): UserInfos[] =>
  Array.from({ length: totalSlots }, (_, index) => {
    const item = items[index];
    return item ? { ...createEmptyUserInfos(), ...item } : createEmptyUserInfos();
  });

export const buildRegisteringPeopleFormValues = (saved: UserInfos[], totalSlots: number) => {
  const shareholders = padUserInfosList(saved, totalSlots);
  return {
    shareholders,
    fileNames: collectShareholderFileNames(shareholders)
  };
};

// Função para transformar ShareholderData em UserInfos
export const mapShareholderDataToUserInfos = async (shareholder: ShareholderData): Promise<UserInfos> => {
  const address = firstRecord(shareholder.address);
  const passport = firstRecord(shareholder.passports);
  const document = firstRecord(shareholder.documents);
  const phone = firstRecord(shareholder.telephones);

  const convertPassportInFile = await convertObjectToFile(passport);
  const converterDocument1InFile = await convertObjectToFile(document);
  const converterAddressInFile = await convertObjectToFile(address);
  const documentMeta = document.meta_data || {};
  const addressMeta = address.meta_data || {};
  const inferredDocumentType =
    normalizeDocumentType(document.document_type) ||
    normalizeDocumentType(documentMeta.document_type) ||
    (typeof document.description === 'string' &&
    /driver licence|driver license|carteira de motorista|licencia/i.test(document.description)
      ? 'drivers_license'
      : document.description
        ? 'national_id'
        : '');

  return {
    id: shareholder.id,
    name: shareholder.name || '',
    occupation: shareholder.occupation || '',
    document_number: shareholder.document_number || '',
    documentType: inferredDocumentType,
    addressProofOwner: ownerFromBelongsToShareholder(address.belongs_to_shareholder, addressMeta.address_proof_owner),
    addressProofKinship: address.relationship_degree || addressMeta.address_proof_kinship || '',
    gender: shareholder.gender || '',
    dateOfBirth: formatApiDateToInput(shareholder.date_of_birth || shareholder.dateOfBirth),
    phone: formatNationalPhone(phone.DDD, phone.number),
    ddi: phone.DDI ? String(phone.DDI) : '',
    email: shareholder.email || '',
    nationality: shareholder.nationality || '',
    passportNo: passport.number || '',
    expiryDate: formatApiDateToInput(passport.expiry_date),
    passportIssueCountry: passport.issuing_country || '',
    issueDate: formatApiDateToInput(passport.issuing_date),
    address1: address.address || '',
    address2: address.complement || '',
    postalCode: address.postal_code || '',
    city: address.city || '',
    state: address.state || '',
    neighborhood: address.neighborhood || '',
    country: address.country || '',
    shareIssuanceDate: formatApiDateToInput(shareholder.shareIssuanceDate),
    classOfShares: shareholder.classOfShares || '',
    numberOfShares: toShareCountInput(shareholder.number_of_shares),
    sharesValue: shareholder.sharesValue != null ? String(shareholder.sharesValue) : '',
    privileges: shareholder.privileges,
    conditions: shareholder.conditions,
    passport: convertPassportInFile || null,
    document1: converterDocument1InFile || null,
    proveAddress: converterAddressInFile || null,
    passportExistingName: fileNameFromDocument(passport),
    document1ExistingName: fileNameFromDocument(document),
    proveAddressExistingName: fileNameFromDocument(address)
  };
};

// Array com os nomes de todos os campos que deseja validar
export const shareholderFields: (keyof UserInfos)[] = [
  'name',
  'occupation',
  'documentType',
  'document_number',
  'addressProofOwner',
  'addressProofKinship',
  'gender',
  'dateOfBirth',
  'phone',
  'ddi',
  'email',
  'nationality',
  'passportNo',
  'expiryDate',
  'passportIssueCountry',
  'issueDate',
  'address1',
  'address2',
  'postalCode',
  'city',
  'state',
  'neighborhood',
  'country',
  'numberOfShares',
  'shareIssuanceDate',
  'classOfShares',
  'sharesValue'
];
